import { useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Globe,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  useAddKbSource,
  useDeleteKbSource,
  useKbSources,
  useRefreshKbOnImport,
  useSyncKbSource,
} from '@/api/kb';
import { ApiError } from '@/api/client';
import type { KbWebSource } from '@/api/types';
import { Badge, Button, Card, Input, Select, useToast } from '@/components/ui';
import { formatRelative } from '@/lib/format';

/**
 * Knowledge base → Website sources: paste a link, the server reads the site and imports its pages
 * as articles in their own category. The AI agent answers from them alongside written articles.
 */
export function WebsiteSources({ onOpenCategory }: { onOpenCategory: (categoryId: string) => void }) {
  const toast = useToast();
  const sources = useKbSources();
  const add = useAddKbSource();
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(50);
  useRefreshKbOnImport(sources.data);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = url.trim();
    if (!value) return;
    add.mutate(
      { url: /^https?:\/\//i.test(value) ? value : `https://${value}`, maxPages },
      {
        onSuccess: () => {
          setUrl('');
          toast.success('Reading the website — this takes a minute');
        },
        onError: (err) => {
          const fields = err instanceof ApiError ? err.fieldErrors : {};
          toast.error(Object.values(fields)[0] ?? err.message);
        },
      },
    );
  };

  const list = sources.data ?? [];
  return (
    <Card className="mb-6 overflow-hidden">
      <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-end">
        <div className="min-w-0 lg:w-80 lg:shrink-0">
          <p className="flex items-center gap-2 font-semibold text-slate-900">
            <Sparkles className="text-brand-500 h-4 w-4" /> Learn from your website
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Paste your site’s link. We read its pages and your AI agent answers from them together with your
            articles.
          </p>
        </div>
        <form onSubmit={submit} className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
          <div className="relative min-w-0 flex-1">
            <Globe className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              aria-label="Website link"
              placeholder="https://kollabr.in"
              inputMode="url"
              className="pl-9"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <Select
            aria-label="Pages to read"
            wrapperClassName="sm:w-36"
            value={maxPages}
            onChange={(e) => setMaxPages(Number(e.target.value))}
          >
            {[10, 25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                Up to {n} pages
              </option>
            ))}
          </Select>
          <Button type="submit" loading={add.isPending} disabled={!url.trim()}>
            Import website
          </Button>
        </form>
      </div>
      {list.length > 0 && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {list.map((s) => (
            <SourceRow key={s.id} source={s} onOpenCategory={onOpenCategory} />
          ))}
        </ul>
      )}
      <p className="border-t border-slate-100 bg-slate-50/60 px-4 py-2 text-xs text-slate-500 sm:px-5">
        Only pages on the same site (and under the link’s path) are read, following robots.txt. Imported pages
        aren’t listed in the public help center. Re-import after your site changes.
      </p>
    </Card>
  );
}

function SourceRow({
  source: s,
  onOpenCategory,
}: {
  source: KbWebSource;
  onOpenCategory: (categoryId: string) => void;
}) {
  const toast = useToast();
  const sync = useSyncKbSource();
  const remove = useDeleteKbSource();
  const [confirm, setConfirm] = useState(false);
  const crawling = s.status === 'crawling';

  return (
    <li className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:px-5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={s.url}
            target="_blank"
            rel="noreferrer noopener"
            className="hover:text-brand-700 inline-flex min-w-0 items-center gap-1 truncate font-medium text-slate-800"
          >
            {s.name} <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
          </a>
          {crawling ? (
            <Badge tone="blue">
              <Loader2 className="h-3 w-3 animate-spin" /> Reading
              {s.pageCount ? ` · ${s.pageCount} pages` : '…'}
            </Badge>
          ) : s.status === 'ready' ? (
            <Badge tone="green">
              <CheckCircle2 className="h-3 w-3" /> {s.pageCount} pages · {s.articleCount} articles
            </Badge>
          ) : (
            <Badge tone="red">
              <AlertTriangle className="h-3 w-3" /> Failed
            </Badge>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-slate-500">
          {s.status === 'failed' && s.error
            ? s.error
            : crawling
              ? `Started ${formatRelative(s.syncStartedAt ?? s.updatedAt)} · up to ${s.maxPages} pages`
              : s.lastSyncedAt
                ? `Imported ${formatRelative(s.lastSyncedAt)}${s.skippedCount ? ` · ${s.skippedCount} pages skipped` : ''}`
                : ''}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {s.categoryId && s.status !== 'crawling' && (
          <Button variant="ghost" size="sm" onClick={() => onOpenCategory(s.categoryId!)}>
            View articles
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          disabled={crawling}
          loading={sync.isPending}
          onClick={() =>
            sync.mutate(s.id, {
              onSuccess: () => toast.success('Re-importing the website'),
              onError: (e) => toast.error(e.message),
            })
          }
        >
          <RefreshCw className="h-3.5 w-3.5" /> Re-import
        </Button>
        <Button
          variant={confirm ? 'danger' : 'ghost'}
          size="sm"
          loading={remove.isPending}
          onClick={() =>
            confirm
              ? remove.mutate(s.id, {
                  onSuccess: () => toast.success('Website and its articles removed'),
                  onError: (e) => toast.error(e.message),
                })
              : setConfirm(true)
          }
          onBlur={() => setConfirm(false)}
          aria-label={confirm ? 'Confirm remove' : `Remove ${s.name}`}
        >
          <Trash2 className="h-3.5 w-3.5" /> {confirm ? 'Remove with its articles' : ''}
        </Button>
      </div>
    </li>
  );
}
